import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import {
  createAdjustment,
  listAdjustments,
  updateAdjustment,
  deleteAdjustment,
  downloadAdjustmentsExcel
} from '../Api/Adjustment/AdjustmentApi';
import { addError } from './Error';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { User } from './User';
import { DrawerData } from './Drawer';
import { blobToExcel } from './GeneralStore';

export interface Adjustment {
  commissionAdjustmentId: number;
  adjustmentTagId: string | null;
  assetUpstackId: string | null;
  assetNumber: string | null;
  customerName: string | null;
  customerId: string | null;
  runDate: string;
  runName: string;
  tallyRunId: number;
  runStatus: string;
  commissionsStatus: string | null;
  salesAgentName: string | null;
  tsdName: string | null;
  actualFirstCommissionRun: string | null;
  actualDecommissionedDate: string | null;
  rpmSupplierName: string | null;
  supplierName: string | null;
  agencyName: string | null;
  adjustmentType: string;
  grossComm: number | null;
  salesComm: number | null;
  netBilled: number | null;
  adjustmentNote: string | null;
  note: string | null;
  taggedBy: User | null;
  taggedAt: string;
  modifiedBy: User | null;
  createdAt: string;
  updatedAt: string;
}

export const adjustmentCurrencyFields: (keyof Adjustment)[] = ['grossComm', 'salesComm', 'netBilled'];

const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const tallyAdjustmentsSubject = new BehaviorSubject<PaginatedResponse<Adjustment>>({} as PaginatedResponse<Adjustment>);
const tallyAdjustmentsLoadingSubject = new BehaviorSubject<boolean>(false);
const currentFiltersSubject = new BehaviorSubject<ApiAdvancedFilter | undefined>(undefined);
const adjustmentSubject = new BehaviorSubject<DrawerData | undefined>(undefined);
const clearError = () => addError('adjustment', undefined);

export const getTallyAdjustmentsSubject = (): BehaviorSubject<PaginatedResponse<Adjustment>> => tallyAdjustmentsSubject;
export const getTallyAdjustmentsLoadingSubject = (): BehaviorSubject<boolean> => tallyAdjustmentsLoadingSubject;
export const getAdjustmentSubject = (): BehaviorSubject<DrawerData | undefined> => adjustmentSubject;

export const fetchAdjustments = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (tallyAdjustmentsSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  tallyAdjustmentsLoadingSubject.next(true);
  currentFiltersSubject.next(filters);
  const { data, error } = await listAdjustments(filters);
  const currentFetchNumber = inboundFetchNumberSubject.getValue();

  if (data && newFetchNumber === currentFetchNumber) {
    clearError();
    tallyAdjustmentsSubject.next(data);
  }

  if (error) addError('adjustment', error.message);

  newFetchNumber === currentFetchNumber && tallyAdjustmentsLoadingSubject.next(false);
};

export const downloadExcel = (tabName: string) => async () => {
  const { data, error } = await downloadAdjustmentsExcel(currentFiltersSubject.getValue());
  if (error) {
    addError('adjustment', error.message);
  } else if (data) {
    const fileName = `Tally_${tabName.replace(/\s+/g, '_')}_Adjustment_Tags_${new Date().toISOString().split('T')[0].replace(/-/g, '_')}.xlsx`;
    blobToExcel(data, fileName);
  }
};

export const getAdjustmentById = async (id: number): Promise<Adjustment> => {
  const params = { filters: [{ field: 'commissionAdjustmentId', operator: '=', value: id }] };
  adjustmentSubject.next(undefined);
  const { data, error } = await listAdjustments(params);
  if (error) addError('adjustment', error.message);
  if (data && !data.data.length) addError('adjustment', 'Adjustment not found');
  if (data && data.data.length) {
    clearError();
    adjustmentSubject.next(data.data[0]);
    return data.data[0];
  }
  return {} as Adjustment;
};

export const saveAdjustment = async (adjustmentUpdate: Adjustment) => {
  const { data, error } = await updateAdjustment(adjustmentUpdate);
  if (data) {
    clearError();
    await getAdjustmentById(adjustmentUpdate.commissionAdjustmentId);
    fetchAdjustments(currentFiltersSubject.getValue(), true);
  }
  if (error) addError('adjustment', error.message);
  return { data, error };
};

export const createAdjustmentTag = async (adjustmentData: Partial<Adjustment>) => {
  const { data, error } = await createAdjustment(adjustmentData);
  if (data && adjustmentData.commissionAdjustmentId) {
    clearError();
    await getAdjustmentById(adjustmentData.commissionAdjustmentId);
    fetchAdjustments(currentFiltersSubject.getValue(), true);
  }
  if (error) addError('adjustment', error.message);
  return { data, error };
};

export const nextAdjustmentDetails = (data: Adjustment, update = true) => {
  const filtered =
    tallyAdjustmentsSubject
      .getValue()
      ?.data?.filter((adj) => adj.tallyRunId === data.tallyRunId && !adj.adjustmentTagId) || [];

  const currentIndex = filtered.findIndex((adj) => adj.commissionAdjustmentId === data.commissionAdjustmentId);
  if (currentIndex === -1 || currentIndex === filtered.length - 1) return null;
  const nextAdjustment = filtered[currentIndex + 1];
  if (update) {
    adjustmentSubject.next(nextAdjustment);
  }
  return nextAdjustment;
};

export const deleteAdjustmentTag = async (adjustment: Adjustment) => {
  if (!adjustment.adjustmentTagId) return;
  const { error } = await deleteAdjustment(adjustment.adjustmentTagId);
  if (error) {
    addError('adjustment', error.message);
  } else {
    clearError();
    await getAdjustmentById(adjustment.commissionAdjustmentId);
    fetchAdjustments(currentFiltersSubject.getValue(), true);
  }
};
